/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react';

import axios from 'axios';
import PhoneInput from 'components/signup-input/phone-input';
import SignupInput from 'components/signup-input/signup-input';
import { MutableState, Tools } from 'final-form';
import { addToCart } from 'helpers/google-ecommerce/events/addToCart';
import { impressions } from 'helpers/google-ecommerce/events/impressions';
import { removeFromCart } from 'helpers/google-ecommerce/events/removeFromCart';
import MixPanelHelper from 'helpers/mixpanel';
import { AsYouType } from 'libphonenumber-js';
import { INITIAL_ALERTS_STATE } from 'pages/leads/pages/pre-signup/constants';
import { func } from 'prop-types';
import { Form } from 'react-final-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParam } from 'react-use';
import { termsOfServiceVersion } from 'utils/envs';
import { withError, withFieldError } from 'utils/old-ui-commons/final-form-states';
import { customMutators } from 'utils/old-ui-commons/form';
import useFetch from 'utils/old-ui-commons/use-fetch';
import { isEmail } from 'utils/old-ui-commons/validations';
import {
  BR_PHONE_PATTERN,
  ICountryPhone,
  MX_PHONE_PATTERN,
  US_PHONE_PATTERN,
  defaultInitialPhoneValues,
  getPhoneInformationObject,
} from 'utils/phone-patterns';
import { isValidSetupUrl } from 'utils/setup-url-whitelist';
import { v4 as uuid } from 'uuid';

import { Box, Button, Flex, Input, Text, getColor } from '@olist/united';

import { ILeadInfo } from '../../../../../olist-types/LeadInfo';
import {
  API_URLS,
  MIXPANEL_EVENTS,
  MOBILE_PRIVACY_POLICY_URL,
  MOBILE_USER_AGREEMENT_URL,
  PRIVACY_POLICY_URL,
  USER_AGREEMENT_URL,
  WEBAPP_VIEWS_URLS,
  checkSourceTypeParam,
  isMobileSource,
} from '../../constants';
import { sumTotalLeadFields } from '../../helpers/gtm-check-input';
import { getLeadTracking } from '../../utils';
import { RootContext } from '../leads-provider';
import SignupRadio from '../signup-radio/signup-radio';
import { getAdditionalProductProperties, getPreSignUpProduct } from './gtm-utils';
import { CustomLabel, StyledForm, TextLink } from './pre-signup-form.style';
import SelectIntegratorField from './select-integrator-field';
import formValidator, { CNPJ_MASK } from './validator';

const InputWith = withError(withFieldError(Input));
const labelErrors = (errors: string[]) =>
  errors.map((error) => (
    <Text key={uuid()} fontSize="12px" mt={1} color="foundation.shadeDark">
      {error}
    </Text>
  ));

const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

const PreSignUpForm = ({ handleFormError }: { handleFormError: Function }) => {
  const { t } = useTranslation();

  const {
    callIntercomBoot,
    clearLeadFieldCountAndCategory,
    setLeadIdCookie,
    setLeadProfileCookie,
    setLeadTotalFields,
  } = useContext(RootContext);

  const LEAD_PROFILE_URL = 'lead-profile';

  const emailParam = useSearchParam('email');
  const sourceParam = useSearchParam('source');

  const [leadInfo, setLeadInfo] = useState<ILeadInfo>();
  const [totalFields, setTotalFields] = useState(0);

  const [phoneCountry, setPhoneCountry] = useState<ICountryPhone>(defaultInitialPhoneValues);
  const postPreSignupInfo = () => axios.post(API_URLS.preSignup, { ...leadInfo });
  // @ts-ignore
  const [{ error, errorResult, isLoading, result, success }, callAPI] = useFetch(postPreSignupInfo);

  const isValidEmailParam = emailParam && isEmail(emailParam);
  const showEmailInput = !emailParam || !isValidEmailParam;
  const inputErrorStyle = {
    borderColor: getColor('red', 'light', 1),
    outlineColor: getColor('red', 'light', 1),
  };

  const userAgreementUrl = isMobileSource(sourceParam)
    ? MOBILE_USER_AGREEMENT_URL
    : USER_AGREEMENT_URL;
  const privacyPolicyUrl = isMobileSource(sourceParam)
    ? MOBILE_PRIVACY_POLICY_URL
    : PRIVACY_POLICY_URL;

  useEffect(() => {
    if (leadInfo && Object.keys(leadInfo).length) {
      // @ts-ignore
      callAPI();
    }
  }, [leadInfo]);

  const getLeadMixpanelInstance = (leadId: string) => {
    const userData = {
      createdAt: Date.now(),
      email: leadInfo?.email,
      id: leadId,
      lastLogin: null,
      name: leadInfo?.name,
      planType: null,
      signupOrigin: 'self-signup',
      termsOfService: termsOfServiceVersion,
      mobile_phone: leadInfo?.mobile_phone,
    };
    return MixPanelHelper(userData)?.getInstance();
  };

  const registerMixpanelLead = (leadId: string) => {
    const mixpanelInstance = getLeadMixpanelInstance(leadId);

    mixpanelInstance?.track(MIXPANEL_EVENTS.PRE_SIGNUP_COMPLETED, {
      is_already_online_seller: leadInfo?.is_already_online_seller,
      integrator: leadInfo?.integrator || null,
    });
  };

  const sendGtmCartEvent = (lead: Partial<ILeadInfo>, isRemove = false) => {
    const isMobile = isMobileSource(sourceParam);
    const additionalProperties = getAdditionalProductProperties(lead);
    const newProduct = getPreSignUpProduct(isMobile, additionalProperties);

    const eventSetup = {
      products: [newProduct],
    };

    if (isRemove) {
      removeFromCart(eventSetup);
      return;
    }

    addToCart(eventSetup);
  };

  const handleFormErrorAlerts = () => {
    scrollToTop();

    if (leadInfo) {
      sendGtmCartEvent(leadInfo, true);
    }

    if (errorResult.response.status === 409) {
      const { type } = errorResult.response.data;

      switch (type) {
        case 'email':
          handleFormError({
            ...INITIAL_ALERTS_STATE,
            showInvalidEmail: true,
          });
          setTimeout(
            () => window.location.assign(`${WEBAPP_VIEWS_URLS.login}?email=${leadInfo?.email}`),
            3000
          );
          break;

        case 'cnpj':
          handleFormError({
            ...INITIAL_ALERTS_STATE,
            showInvalidCnpj: true,
          });
          break;

        default:
          break;
      }
    } else {
      handleFormError({
        ...INITIAL_ALERTS_STATE,
        showFormError: true,
      });
    }
  };

  const onSubmit = (lead: Partial<ILeadInfo>) => {
    const phone = getPhoneInformationObject(lead.mobile_phone || '', phoneCountry);
    const newLeadData = { ...lead };
    newLeadData.mobile_phone = phone.getNumber()?.number;
    const urlSourceParam = checkSourceTypeParam(sourceParam);
    const leadMarketingTracking = getLeadTracking(urlSourceParam);
    const integratorsData =
      lead?.is_already_online_seller === '1'
        ? { integrator: newLeadData?.integrator }
        : { integrator: undefined };

    sendGtmCartEvent(newLeadData);

    setLeadInfo({
      ...leadMarketingTracking,
      ...newLeadData,
      ...(isValidEmailParam && { email: emailParam }),
      ...{ is_already_online_seller: !!Number.parseInt(lead.is_already_online_seller || '0', 2) },
      ...integratorsData,
    });

    const sumTotalFields = sumTotalLeadFields(lead as any);
    clearLeadFieldCountAndCategory();
    setTotalFields(sumTotalFields);
  };

  const trackExperimentAndRedirect = (leadId: string, redirectTo: string, redirect: Function) => {
    const leadMixpanelInstance = getLeadMixpanelInstance(leadId);
    if (redirectTo.match(LEAD_PROFILE_URL)) {
      leadMixpanelInstance?.track(
        MIXPANEL_EVENTS.EXPERIMENT_STARTED,
        MIXPANEL_EVENTS.EXPERIMENT_SKU_VARIANT
      );
    } else {
      leadMixpanelInstance?.track(
        MIXPANEL_EVENTS.EXPERIMENT_STARTED,
        MIXPANEL_EVENTS.EXPERIMENT_SKU_ORIGINAL
      );
    }
    setTimeout(() => {
      redirect();
    }, 1000);
  };

  useEffect(() => {
    if (success) {
      const { email, name } = leadInfo!;
      const { lead_id: leadId, redirectTo } = result.data;
      const leadProfile = { id: leadId, name, email, createdAt: new Date() };
      registerMixpanelLead(leadId);
      callIntercomBoot(leadProfile);
      setLeadIdCookie(leadId);
      setLeadProfileCookie(leadProfile);
      setLeadTotalFields(totalFields);

      const redirectUrl = `${redirectTo}${window.location.search}`;
      if (isValidSetupUrl(redirectTo)) {
        trackExperimentAndRedirect(leadId, redirectUrl, () => window.location.assign(redirectUrl));
      }
    }
  }, [success]);

  useEffect(() => {
    if (error && !isLoading) {
      handleFormErrorAlerts();
    }
  }, [error, isLoading]);

  useEffect(() => {
    const isMobile = isMobileSource(sourceParam);
    const product = getPreSignUpProduct(isMobile);

    impressions([product]);
  }, []);

  const handleChangePhoneCountry = (phoneParams: ICountryPhone) => {
    new AsYouType().reset();
    setPhoneCountry(phoneParams);
  };

  const handleOnChangePhoneNumber = (
    args: any,
    state: MutableState<object, object>,
    utils: Tools<object, any>
  ) => {
    const input = args[0]?.target?.value;
    const phone = new AsYouType(phoneCountry.code as any).input(input);
    utils.changeValue(state, 'mobile_phone', () => phone);
  };

  const handleOnChangeCNPJ = (
    args: any,
    state: MutableState<object, object>,
    utils: Tools<object, any>
  ) => {
    const cnpj = args[0]?.target?.value;
    utils.changeValue(state, 'cnpj', () => cnpj);
  };

  const handleDropdownItemsOptions = (
    value: string,
    prefixNumber: string,
    code: string,
    placeHolder: string,
    mask: any,
    form: any
  ) => ({
    value,
    onClick: () => {
      form.mutators.setValue('mobile_phone', '');
      handleChangePhoneCountry({
        prefixNumber,
        code,
        mask,
        placeHolder,
      });
    },
  });

  return (
    <Flex flexDirection="column" alignItems="center" mt={5}>
      <Form
        mutators={{
          setValue: customMutators.setValue,
          handleOnChangePhoneNumber,
          handleOnChangeCNPJ,
        }}
        onSubmit={onSubmit}
        initialValues={{ email: isValidEmailParam ? emailParam : '' }}
        validate={(values) => formValidator(values, phoneCountry)}
        render={({ form, handleSubmit, invalid, ...formState }) => {
          const hasAlreadySellerFieldError =
            formState.dirty && formState?.errors?.is_already_online_seller;
          return (
            <StyledForm onSubmit={handleSubmit}>
              <Box minHeight="60px" my={3}>
                <CustomLabel htmlFor="is_already_online_seller" danger={hasAlreadySellerFieldError}>
                  {t`do you currently sell online?`}
                </CustomLabel>
                <Flex flexWrap="wrap" margin="-5px">
                  <Box m="5px">
                    <SignupRadio
                      name="is_already_online_seller"
                      label={t`yes, already selling online`}
                      value="1"
                      data-testid="isOnlineSeller"
                    />
                  </Box>
                  <Box m="5px">
                    <SignupRadio
                      name="is_already_online_seller"
                      label={t`no, i want to start`}
                      value="0"
                      role="radio"
                    />
                  </Box>
                  <SelectIntegratorField name="integrator" label={t`do you use any integrator?`} />
                </Flex>
              </Box>
              <Box minHeight="90px">
                <SignupInput
                  name="name"
                  id="name"
                  required
                  component={InputWith}
                  handleLabel={
                    <CustomLabel htmlFor="name">{t`responsible persons name`}</CustomLabel>
                  }
                  initialErrors={[{}]}
                  mask={false}
                  onBlur={() => true}
                  handleErrorMessages={labelErrors}
                  styleOnError={inputErrorStyle}
                />
              </Box>
              {showEmailInput && (
                <Box minHeight="90px">
                  <SignupInput
                    name="email"
                    id="email"
                    initialErrors={[{}]}
                    mask={false}
                    onBlur={() => true}
                    type="email"
                    component={InputWith}
                    handleLabel={<CustomLabel htmlFor="email">{t`company email`}</CustomLabel>}
                    handleErrorMessages={labelErrors}
                    styleOnError={inputErrorStyle}
                  />
                </Box>
              )}
              <Box mb={4}>
                <CustomLabel htmlFor="mobile_phone">{t`mobile phone`}</CustomLabel>
                <PhoneInput
                  id="mobile_country_code"
                  name="mobile_country_code"
                  defaultValue={phoneCountry.prefixNumber}
                  items={[
                    handleDropdownItemsOptions(
                      '+55 Brasil',
                      '+55',
                      'BR',
                      '(99) 99999-9999',
                      BR_PHONE_PATTERN,
                      form
                    ),
                    handleDropdownItemsOptions(
                      '+52 Mexico',
                      '+52',
                      'MX',
                      '999 999 9999',
                      MX_PHONE_PATTERN,
                      form
                    ),
                    handleDropdownItemsOptions(
                      '+1 Usa',
                      '+1',
                      'US',
                      '(999) 999-9999',
                      US_PHONE_PATTERN,
                      form
                    ),
                  ]}
                  handleOnChangeInput={form.mutators.handleOnChangePhoneNumber}
                  phoneValue={phoneCountry}
                  inputErrorStyle={inputErrorStyle}
                  labelErrors={labelErrors}
                />
              </Box>
              <Box minHeight="90px" mb={5}>
                <SignupInput
                  name="cnpj"
                  id="cnpj"
                  placeholder="00.000.000/0000-00"
                  handleLabel={<CustomLabel htmlFor="cnpj">{t`cnpj`}</CustomLabel>}
                  mask={CNPJ_MASK}
                  initialErrors={[{}]}
                  onBlur={() => true}
                  guide={false}
                  handleErrorMessages={labelErrors}
                  styleOnError={inputErrorStyle}
                  onChange={form.mutators.handleOnChangeCNPJ}
                />
              </Box>
              <Box mb="12px">
                <Button
                  loading={isLoading}
                  disabled={invalid || isLoading}
                  type="submit"
                  fullWidth
                  variation="primary"
                >
                  {t`continue`}
                </Button>
              </Box>
              <Box mb="20px">
                <Text color="foundation.shadeDark" fontSize="12px" textAlign="center">
                  <Trans i18nKey="policyAgree">
                    by continuing you agree to <b>olists</b>
                    <TextLink
                      color="foundation.shadeDark"
                      textDecoration="underline"
                      href={userAgreementUrl}
                    >
                      terms of use
                    </TextLink>
                    and
                    <TextLink
                      color="foundation.shadeDark"
                      textDecoration="underline"
                      href={privacyPolicyUrl}
                    >
                      privacy policy
                    </TextLink>
                  </Trans>
                </Text>
              </Box>
            </StyledForm>
          );
        }}
      />
    </Flex>
  );
};

PreSignUpForm.propTypes = {
  handleFormError: func.isRequired,
};

export default PreSignUpForm;
